import { notification } from 'antd';
import type { ReactNode } from 'react';

import globalVars from './global.js';

export type MutableObject = Record<string, unknown>;

export function alertError(description: ReactNode): void {
  const now = Date.now();
  if (now < globalVars.alertErrorTime) return;
  globalVars.alertErrorTime = now + 4500;
  notification.error({ message: description });
}

export function alertSuccess(message: ReactNode): void {
  notification.success({ message });
}

export function alertInfo(message: ReactNode): void {
  notification.info({ message: '提示', description: message });
}

export function makeSendObject<T extends MutableObject>(
  input: T | null | undefined,
  specials?: readonly string[],
  notRoot = false,
): T | null | undefined {
  if (!input) return input;
  const object: MutableObject = notRoot
    ? input
    : (JSON.parse(JSON.stringify(input)) as MutableObject);
  for (const key of Object.keys(object)) {
    if (key.startsWith('_')) {
      delete object[key];
      continue;
    }
    if (specials?.includes(key)) continue;
    const value = object[key];
    if (value) {
      if (typeof value === 'object' && value !== null && 'id' in value) {
        object[key] = value.id;
        continue;
      }
    } else if (value === null) {
      object[key] = 0;
    }
    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'object' && item !== null) {
            makeSendObject(item as MutableObject, undefined, true);
          }
        }
      } else {
        makeSendObject(value as MutableObject, undefined, true);
      }
    }
  }
  return object as T;
}

function getJsDateFromExcel(excelDate: number): Date {
  const offset = new Date().getTimezoneOffset() * 60000;
  return new Date((excelDate - (25567 + 2)) * 86400 * 1000 + offset);
}

export interface EditInputOptions extends MutableObject {
  type?: string;
  options?: unknown;
  size?: unknown;
  step?: unknown;
  unit?: unknown;
  modal?: unknown;
  tagKey?: unknown;
  fileType?: unknown;
}

export const editTools = {
  cloneObject<T>(object: T | null | undefined): T | undefined {
    if (!object) return undefined;
    return JSON.parse(JSON.stringify(object)) as T;
  },

  writeBackObject(fromObject: MutableObject, toObject: MutableObject): void {
    for (const key of Object.keys(fromObject)) toObject[key] = fromObject[key];
  },

  parseUserInputDate(dateString: unknown): Date | null {
    if (!dateString) return null;
    if (dateString.toString().match(/^[\d]+$/u)) {
      return getJsDateFromExcel(Number(dateString));
    }
    if (typeof dateString !== 'string' || dateString.length === 0) return null;

    let match = dateString.match(/^\s*([\d]{4})[^\d]*([\d]{1,2})[^\d]*([\d]{1,2})\s*$/u);
    if (match?.length === 4) return new Date(`${match[1]}-${match[2]}-${match[3]}`);
    match = dateString.match(/^\s*([\d]{1,2})\/([\d]{1,2})\/([\d]{4})\s*$/u);
    if (match?.length === 4) return new Date(`${match[3]}-${match[1]}-${match[2]}`);
    match = dateString.match(/^\s*([\d]{1,2})\/([\d]{1,2})\/([\d]{1,2})\s*$/u);
    if (!match || match.length !== 4) throw new Error(`${dateString} 不可识别的日期格式!`);
    return new Date(`20${match[3]}-${match[1]}-${match[2]}`);
  },

  guessInputTypeByOptions(options: EditInputOptions): EditInputOptions {
    let { type } = options;
    if (!type) {
      if (options.options) type = 'select';
      else if (options.size) type = 'image';
      else if (options.step || options.unit) type = 'number';
      else if (options.modal) type = 'selectModal';
      else if (options.tagKey) type = 'tagSelect';
      else if (options.fileType) type = 'fileDirectory';
      else type = 'text';
      options.type = type;
    }
    return options;
  },

  makeSendObject,
};

export function debounce<Arguments extends unknown[]>(
  action: (...args: Arguments) => void,
  delay: number,
): (...args: Arguments) => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return function debounced(this: unknown, ...args: Arguments): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => action.apply(this, args), delay);
  };
}

export function parHtmlIt(valueHtml: string): string {
  let value = valueHtml.split('  ').join('').split('\t').join('');
  for (let index = 1; index < 6; index += 1) {
    const hashes = '#'.repeat(index);
    value = value.split(`<h${index}>`).join(`\n\n${hashes} `);
    value = value.split(`</h${index}>`).join('\n\n');
  }
  value = value.split('<i class="emoji" contenteditable="false">').join('');
  const emojiEndParts = value.split('</i>');
  if (emojiEndParts.length === value.split('<i class="emoji" contenteditable="false">').length) {
    value = emojiEndParts.join('');
  }
  const katexMaths = value.split('<span class="katex-mathml">');
  if (katexMaths.length) value = katexMaths.join('<code lang="math">');
  const katexMathEnds = value.split('</span>');
  if (katexMaths.length === katexMathEnds.length) value = katexMathEnds.join('</code>');

  for (const language of ['c', 'cpp', 'python']) {
    const languageParts = value.split(`<pre data-language="${language}"><code><li class="code">`);
    if (languageParts.length) value = languageParts.join(`\n\n\`\`\`${language}\n`);
    const languagePartEnds = value.split('</li></code></pre>');
    if (languageParts.length === languagePartEnds.length) value = languagePartEnds.join('\n```\n');
  }

  const replacements: readonly [string, string][] = [
    ['<ul><li>', '\n\n*. '],
    ['</li></ul>', '\n'],
    ['<blockquote>', '\n\n> '],
    ['</blockquote>', '\n'],
    ['&amp;', '&'],
    ['&#x27;', "'"],
    ['&quot;', '"'],
    ['<li class="code">', ''],
  ];
  for (const [from, to] of replacements) value = value.split(from).join(to);
  if (valueHtml.includes('<li class="code">')) value = value.split('</li>').join('');
  return value;
}
